import { displayLabel } from "../../utils/displayLabel";
import React, { useEffect, useState } from "react";
import { BookOpen, Bookmark, ShoppingBag } from "lucide-react";
import { Link } from "react-router-dom";
import "./MyCircles.css";
import "./MyOrders.css";

import { getReaderOrders } from "../../services/readerOrders";
import { getOrderTracking } from "../../utils/readerOrderTracking";

const hasEbook = order => (order.items || []).some(item => String(item.format || '').toLowerCase().replace(/\s+/g, '') === 'ebook');

export default function MyOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    const controller = new AbortController();
    setOrders([]);
    setLoading(true);
    setError("");
    getReaderOrders({ signal: controller.signal })
      .then((data) => { if (!controller.signal.aborted) setOrders(data); })
      .catch((err) => { if (!controller.signal.aborted) setError(err.message || "We couldn’t load your orders. Please try again."); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [revision]);

  useEffect(() => {
    const refresh = () => { if (!document.hidden) { setOrders([]); setLoading(true); setRevision(value => value + 1); } };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => { window.removeEventListener('focus', refresh); document.removeEventListener('visibilitychange', refresh); };
  }, []);

  const visibleOrders = orders.filter((order) => {
    const tracking = getOrderTracking(order);
    return filter === "all" || (filter === "active" ? tracking.active : filter === "digital" ? hasEbook(order) || ["sent", "link_clicked"].includes(tracking.status) : tracking.status === "delivered");
  });

  return <section className="my-circles reader-orders max-w-6xl mx-auto" aria-label="My orders" aria-busy={loading}>
    <header className="circles-journal-header">
      <div className="circles-book-art" aria-hidden="true"><BookOpen size={76} strokeWidth={1.2}/><span>Stories worth waiting for</span></div>
      <div className="circles-introduction"><p className="circles-kicker">My Orders</p><h2>Your stories, all in one place.</h2><p>A place for your bookish parcels and digital stories. Find your purchases, follow shipping updates, and check on ebook emails.</p></div>
      <button disabled={loading} onClick={() => setRevision(value => value + 1)} className="circles-refresh">Refresh orders</button>
    </header>
    <div className="flex flex-wrap gap-2" aria-label="Filter orders">
      {[["all", "All orders"], ["active", "In progress"], ["delivered", "Delivered"], ["digital", "Ebooks"]].map(([value, label]) =>
        <button key={value} aria-pressed={filter === value} onClick={() => setFilter(value)}
          className={`rounded-full border px-4 py-2 text-sm ${filter === value ? "bg-bookish-purple text-white" : "bg-white text-bookish-purple"}`}>{label}</button>)}
    </div>
    <details className="orders-help">
      <summary>Missing an order or ebook?</summary>
      <div><p>New orders can take a few minutes to appear after payment is confirmed. Use the same verified email address you entered at checkout, then refresh your orders.</p>
      <p>Ebook download links arrive by email. Check spam or promotions, and use the expiry date shown in that email. Save the file to your device before the link expires.</p>
      <p>If an email is missing or a link has expired, contact the author using your confirmation email or their Bookish Circle page. Include the full order reference below so they can find your purchase.</p></div>
    </details>
    {loading ? <p role="status">Loading your orders...</p> : error ? <div role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-4">
      <p>{error}</p><button onClick={() => setRevision((value) => value + 1)} className="mt-2 font-semibold underline">Try again</button>
    </div> : orders.length === 0 ?
      <div className="orders-empty"><ShoppingBag size={44} strokeWidth={1.3} aria-hidden="true"/><h3>No orders yet</h3><p>Your next favorite story is waiting to be found. Purchases appear here once payment is confirmed.</p><Link to="/market">Find your next read in the Market</Link></div> :
      visibleOrders.length === 0 ? <div className="orders-empty"><p role="status">No orders match this filter.</p><button onClick={()=>setFilter("all")}>Show all orders</button></div> :
      visibleOrders.map((order) => {
        const tracking = getOrderTracking(order);
        return <article key={order.orderId} className="reader-order-card">
        <div className="flex flex-wrap justify-between gap-2">
          <h3 className="order-shop-title"><Bookmark size={18} aria-hidden="true"/>{displayLabel(order.authorName || "Author shop")}</h3>
          <span className="rounded-full bg-bookish-cream px-3 py-1 text-sm font-medium">{tracking.label}</span>
        </div>
        <p className="order-reference">Order {String(order.orderId || "").length > 16 ? `\u2026${String(order.orderId).slice(-8)}` : order.orderId}{order.createdAt && !Number.isNaN(Date.parse(order.createdAt)) ? ` · ${new Date(order.createdAt).toLocaleDateString()}` : ""}</p>
        <details className="text-sm"><summary className="cursor-pointer py-2">Full order reference</summary><p className="break-all select-all">{order.orderId}</p></details>
        <p className="text-sm text-gray-600">{tracking.description}</p>
        <ul className="space-y-2">{(order.items || []).map((item, index) => <li key={`${item.bookSlug || "item"}-${index}`}>
          <p className="order-book-title">{displayLabel(item.title || "Book")}</p>
          <p className="order-book-format">{item.format ? displayLabel(item.format) : 'Format not recorded'} · Quantity {item.quantity ?? 1}</p>
        </li>)}</ul>
        {hasEbook(order) && !['cancelled','canceled','refunded','failed','pending'].includes(tracking.status) && <p className="order-ebook-note">For ebook access, use the download link in your email. A shipping status on a mixed order refers to the printed books.</p>}
        {(order.shippo?.trackingNumber || tracking.trackingUrl) && <div className="rounded-xl bg-bookish-cream/40 p-3 space-y-2">
          {order.shippo?.trackingNumber && <p className="text-sm break-all">Tracking number: {order.shippo.trackingNumber}</p>}
          {tracking.trackingUrl && <a href={tracking.trackingUrl} target="_blank" rel="noopener noreferrer" className="inline-block font-semibold text-bookish-purple underline" aria-label={`Track shipment for order ${order.orderId} (opens in a new tab)`}>Track shipment ↗</a>}
        </div>}
      </article>; })}
  </section>;
}
